import React, { Component, Context, ContextType } from 'react';

import { IChangeEvent } from '@rjsf/core';
import Form from '@rjsf/mui';
import {
  RJSFSchema,
  RegistryFieldsType,
  RegistryWidgetsType,
  StrictRJSFSchema,
  TemplatesType,
  UiSchema,
} from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import type { JSONSchema7Definition } from 'json-schema';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { AuthContext, type AuthContextValue } from '~/auth';

import { Alert, Box, Button, Typography } from '@mui/material';

import * as libRegions from '@luxuryescapes/lib-regions';
import {
  REGIONS_COUNTRY_CODES,
  isPhoneNumberValidForRegion,
} from '@luxuryescapes/lib-regions/lib/phoneNumberValidation';

import { ROLE_ICS_STORE_TEAM } from '~/consts/roles';

import type { LanguageOption } from '~/services/LabService';
import { formatDateOrdinalWithClock } from '~/services/TimeService';
import UsersService from '~/services/UsersService';

import { canEnableDisableAccounts } from '~/utils/adminPermission';
import {
  BusinessDetailsForm,
  apiToFormBusinessDetails,
  formToApiBusinessDetails,
} from '~/utils/businessDetailsMapping';
import {
  TRAVEL_PREFERENCE_LIMITS,
  TravelPreferencesForm,
  diffTravelPreferencePatches,
  validateTravelPreferences,
} from '~/utils/travelPreferencesMapping';

import BusinessAddressField from './BusinessAddressWidget';
import GridObjectFieldTemplate from './Forms/GridObjectFieldTemplate';
import LinkedAccountField from './Forms/LinkedAccountField';
import SimpleArrayFieldItemTemplate from './Forms/SimpleArrayFieldItemTemplate';
import SimpleArrayFieldTemplate from './Forms/SimpleArrayFieldTemplate';
import UserDetailsObjectFieldTemplate from './Forms/UserDetailsObjectFieldTemplate';
import FrequentFlyerAirlineWidget from './Forms/widgets/FrequentFlyerAirlineWidget';
import ResetPasswordWidget from './Forms/widgets/ResetPasswordWidget';

const defaultFieldsOrder = [
  'customer_support_code',
  'title',
  'givenName',
  'surname',
  'dob',
  'email',
  'email_verified',
  'phone_prefix',
  'phone',
  'shadow_ban_user',
  'country_code',
  'postcode',
  'language',
  'recently_used_airport_code',
  'resetPassword',
  'account_enabled',
  'password',
  '*',
  'travel_preferences',
  'business_details',
  'partnerships',
];

const DEFAULT_PHONE_FORMAT_HELP_MESSAGE = 'local number only, eg 402123123. No leading 0, no +61, no prefixes.';

const initUiSchema: UiSchema = {
  // Enable 3-column layout (12-col grid, default span = 4 = 33%)
  'ui:options': { gridLayout: true, gridColumns: 3 },
  'ui:order': defaultFieldsOrder,
  // Row 1
  customer_support_code: { 'ui:colSpan': 2, 'ui:readonly': true },
  title: { 'ui:widget': 'select', 'ui:colSpan': 2 },
  // Row 2
  dob: { 'ui:widget': 'date' },
  email: { 'ui:widget': 'email' },
  email_verified: { 'ui:readonly': true },
  // Row 3
  phone: {
    'ui:help': DEFAULT_PHONE_FORMAT_HELP_MESSAGE,
  },
  // Location fields
  country_code: { 'ui:colSpan': 2 },
  postcode: { 'ui:colSpan': 2 },
  language: { 'ui:widget': 'select', 'ui:colSpan': 2 },
  recently_used_airport_code: { 'ui:emptyValue': null, 'ui:colSpan': 2 },
  resetPassword: { 'ui:colSpan': 2 },
  roles: { 'ui:colSpan': 6, 'ui:options': { roleLayout: true } },
  vendors: { 'ui:colSpan': 6 },
  partnerships: {
    'ui:colSpan': 12,
    // Enable grid layout on each individual partnership so their fields are 1/3 width
    kfp: { 'ui:options': { gridLayout: true, gridColumns: 3 } },
    vff: { 'ui:options': { gridLayout: true, gridColumns: 3 } },
  },
};

const FORM_TEMPLATES: Partial<TemplatesType> = {
  ObjectFieldTemplate: GridObjectFieldTemplate,
  ArrayFieldTemplate: SimpleArrayFieldTemplate,
  ArrayFieldItemTemplate: SimpleArrayFieldItemTemplate,
};

const GROUPED_FORM_TEMPLATES: Partial<TemplatesType> = {
  ...FORM_TEMPLATES,
  ObjectFieldTemplate: UserDetailsObjectFieldTemplate,
};

const PHONE_FORMAT_HELP_MESSAGE_BY_REGION = {
  AU: 'AU local number only, e.g. 412345678. No leading 0, no +61, no prefixes.',
  GB: 'GB local number only, e.g. 7123456789. No leading 0, no +44, no prefixes.',
  US: 'US local number only, e.g. 4151234567. No leading 0, no +1, no prefixes.',
  NZ: 'NZ local number only, e.g. 212345678. No leading 0, no +64, no prefixes.',
  SG: 'Singapore local number only, eg. 8021231234. No leading 0, no +65, no prefixes.',
};

function sanitisePhoneNumber(phoneNumber?: string | null) {
  // replace all non-numeric characters with an empty string
  return phoneNumber ? phoneNumber.replace(/[^0-9]/g, '') : '';
}

/**
 * Surfaces every message the API returned instead of only the first one, so field-level
 * validation failures (e.g. "Invalid phone number") are visible rather than a bare status code.
 */
function getApiErrorMessage(err: { errors?: Array<string>; message?: string }) {
  if (err.errors?.length) {
    return err.errors.join('; ');
  }
  return err.message;
}

const EMPTY_LANGUAGE_OPTION: LanguageOption = { value: '', label: 'Not set' };

function getLanguageOptions(languageOptions: Array<LanguageOption>, currentLanguage?: string | null) {
  const options = [EMPTY_LANGUAGE_OPTION, ...languageOptions];
  if (!currentLanguage || options.some(({ value }) => value === currentLanguage)) {
    return options;
  }

  return [...options, { value: currentLanguage, label: currentLanguage }];
}

const customFields: RegistryFieldsType = {
  BusinessAddressField,
  LinkedAccountField,
};

const customWidgets: RegistryWidgetsType = {
  FrequentFlyerAirlineWidget,
};

const travelPreferencesSchema: StrictRJSFSchema = {
  type: 'object',
  title: 'Travel preferences',
  properties: {
    frequent_flyer_numbers: {
      type: 'array',
      title: 'Frequent flyers',
      description: 'Airline loyalty programs and membership numbers.',
      maxItems: TRAVEL_PREFERENCE_LIMITS.maxFrequentFlyerRows,
      items: {
        type: 'object',
        title: 'frequent flyer',
        properties: {
          airline: {
            type: ['string', 'null'],
            title: 'Frequent flyer loyalty',
            maxLength: TRAVEL_PREFERENCE_LIMITS.airlineMaxLength,
            default: null,
          },
          number: {
            type: ['string', 'null'],
            title: 'Frequent flyer number',
            maxLength: TRAVEL_PREFERENCE_LIMITS.numberMaxLength,
            default: null,
          },
        },
      },
    },
    seat_preference: {
      type: ['string', 'null'],
      title: 'Airline seat preference',
      maxLength: TRAVEL_PREFERENCE_LIMITS.seatPreferenceMaxLength,
    },
    special_requests: {
      type: ['string', 'null'],
      title: 'Special requests or preferences',
      maxLength: TRAVEL_PREFERENCE_LIMITS.specialRequestsMaxLength,
    },
  },
};

const travelPreferencesUiSchema: UiSchema = {
  // The section card already carries the heading, and a row heading would only repeat it.
  'ui:title': '',
  frequent_flyer_numbers: {
    // Frequent flyers carry no meaningful order, so the reordering arrows are hidden.
    'ui:options': { orderable: false, compact: true },
    items: {
      'ui:title': '',
      'ui:options': { gridLayout: true, gridColumns: 2 },
      airline: { 'ui:widget': 'FrequentFlyerAirlineWidget' },
    },
  },
  special_requests: { 'ui:widget': 'textarea' },
};
function isEmptyTravelPreferences(preferences?: TravelPreferencesForm) {
  if (!preferences) {
    return true;
  }
  const { frequent_flyer_numbers: frequentFlyerNumbers, seat_preference, special_requests } = preferences;
  return !frequentFlyerNumbers?.length && !seat_preference && !special_requests;
}

type Props = {
  groupedSections?: boolean;
  user: App.User;
  schema: RJSFSchema;
  brand: App.Brands;
  update?: (user: App.User) => void;
  initialBusinessDetails?: BusinessDetailsForm;
  onBusinessDetailsSaved?: () => void;
  initialTravelPreferences?: TravelPreferencesForm;
  isTravelPreferencesLoading?: boolean;
  onTravelPreferencesSaved?: () => void;
  languageOptions?: Array<LanguageOption>;
  linkedAccountEmail?: string;
  linkedAccountId?: string;
  disallowedRoles?: Array<{
    role: string;
    message: string;
  }>;
};

type FormData = Omit<App.User, 'gdpr'> & {
  resetPassword?: boolean;
  password?: string;
  vendors?: Array<string>;
  gdpr: boolean | string;
  business_details?: BusinessDetailsForm;
  travel_preferences?: TravelPreferencesForm;
  linked_personal_account?: string;
  account_enabled?: boolean;
};

type State = {
  formData: FormData;
  schema: RJSFSchema;
  alertSuccess: boolean;
  alertError: boolean;
  errorMessage: string;
  partnershipsAlertError: boolean;
  partnershipsErrorMessage: string;
  flightEnabled: boolean;
  referralProgramEnabled: boolean;
  shadowBanUser: boolean;
  accountEnabled: boolean;
};

export default class UpdateUser extends Component<Props, State> {
  static contextType: Context<AuthContextValue> = AuthContext;
  context: ContextType<typeof AuthContext>;

  submitButton: HTMLButtonElement;
  private draftBaseline?: FormData;

  get hasUnsavedChanges() {
    if (!this.draftBaseline) {
      return false;
    }

    const { formData } = this.handleStateData();
    return !isEqual(
      { ...formData, resetPassword: Boolean(formData.resetPassword) },
      { ...this.draftBaseline, resetPassword: Boolean(this.draftBaseline.resetPassword) },
    );
  }

  constructor(props) {
    super(props);

    const formData: FormData = cloneDeep(this.props.user);
    const schema = cloneDeep(this.props.schema);
    formData.language = formData.language ?? '';
    formData.account_enabled = formData.status === 'ENABLED';

    const mergedRegions = ['US', 'CA'];
    const preferredRegions = ['AU', 'NZ', 'GB', 'SG'];
    const sortedRegionCodes = [
      ...preferredRegions,
      ...Object.keys(REGIONS_COUNTRY_CODES)
        .filter((code) => !preferredRegions.includes(code) && !mergedRegions.includes(code))
        .sort((a, b) => REGIONS_COUNTRY_CODES[a].countryName.localeCompare(REGIONS_COUNTRY_CODES[b].countryName)),
    ];

    const phonePrefixSchemas: Array<JSONSchema7Definition> = sortedRegionCodes.map((regionCode) => {
      const region = REGIONS_COUNTRY_CODES[regionCode];
      const internationalPhonePrefix = region.countryCode;
      const regionName = region.countryName;

      return {
        type: 'string',
        title: `${regionName} - ${regionCode} (+${internationalPhonePrefix})`,
        enum: [internationalPhonePrefix],
      };
    });

    phonePrefixSchemas.splice(2, 0, {
      type: 'string',
      title: 'United States / Canada - US/CA (+1)',
      enum: ['1'],
    });

    schema.properties.phone_prefix = {
      ...(schema.properties.phone_prefix as object),
      oneOf: phonePrefixSchemas,
    };

    // Set default phone prefix to AU if unknown phone_prefix
    if (formData.phone_prefix) {
      const phonePrefixRegion = Object.keys(REGIONS_COUNTRY_CODES).find(
        (regionCode) => REGIONS_COUNTRY_CODES[regionCode].countryCode === formData.phone_prefix,
      );

      if (!phonePrefixRegion) {
        formData.phone_prefix = '61';
      }
    }

    this.state = {
      schema,
      formData,
      alertSuccess: false,
      alertError: !formData.country_code,
      errorMessage: !formData.country_code ? 'Country is a required field!' : '',
      partnershipsAlertError: false,
      partnershipsErrorMessage: '',
      flightEnabled: formData.flights_enabled,
      referralProgramEnabled: formData.referral_program_enabled,
      shadowBanUser: formData.shadow_ban_user,
      accountEnabled: formData.status === 'ENABLED',
    };

    this.customValidate = this.customValidate.bind(this);
  }

  canToggleAccountEnabled() {
    return canEnableDisableAccounts(this.context.user);
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.initialTravelPreferences !== this.props.initialTravelPreferences) {
      const shouldSyncTravelPreferences =
        this.props.initialTravelPreferences && isEmptyTravelPreferences(this.state.formData.travel_preferences);
      if (shouldSyncTravelPreferences) {
        if (this.draftBaseline) {
          this.draftBaseline.travel_preferences = cloneDeep(this.props.initialTravelPreferences);
        }
        this.setState((prevState) => ({
          formData: {
            ...prevState.formData,
            travel_preferences: cloneDeep(this.props.initialTravelPreferences),
          },
        }));
      }
    }

    if (prevProps.initialBusinessDetails !== this.props.initialBusinessDetails) {
      const shouldSyncBusinessDetails =
        this.props.initialBusinessDetails && this.isBusinessDetailsEmpty(this.state.formData.business_details);
      if (shouldSyncBusinessDetails) {
        if (this.draftBaseline) {
          this.draftBaseline.business_details = cloneDeep(this.props.initialBusinessDetails);
        }
        this.setState((prevState) => ({
          formData: {
            ...prevState.formData,
            business_details: this.props.initialBusinessDetails,
          },
        }));
      }
    }
  }

  isBusinessDetailsEmpty(details?: BusinessDetailsForm) {
    if (!details) {
      return true;
    }
    const { abn, businessName, businessPosition, businessAddress } = details;
    const hasAddressValue =
      businessAddress && Object.values(businessAddress).some((value) => value !== null && value !== '');
    return !abn && !businessName && !businessPosition && !hasAddressValue;
  }

  getDisallowedRoleMessage(roles: Array<string>) {
    return this.props.disallowedRoles?.find(({ role }) => roles.includes(role))?.message;
  }

  filterDisallowedRoleOptions(schema: RJSFSchema, selectedRoles: Array<string>) {
    const disallowedRoles = (this.props.disallowedRoles ?? [])
      .map(({ role }) => role)
      .filter((role) => !selectedRoles.includes(role));

    if (disallowedRoles.length === 0) {
      return;
    }

    const rolesSchema = schema.properties.roles as StrictRJSFSchema;
    const roleItemsSchema = rolesSchema?.items as StrictRJSFSchema;
    const roleItemsSchemaWithEnumNames = roleItemsSchema as StrictRJSFSchema & { enumNames?: Array<string> };
    const roleEnums = roleItemsSchema?.enum;

    if (!Array.isArray(roleEnums)) {
      return;
    }

    const enumNames = roleItemsSchemaWithEnumNames.enumNames;
    const filteredRoleEnums = [];
    const filteredRoleEnumNames = [];

    roleEnums.forEach((role, index) => {
      if (typeof role === 'string' && disallowedRoles.includes(role)) {
        return;
      }

      filteredRoleEnums.push(role);
      if (Array.isArray(enumNames)) {
        filteredRoleEnumNames.push(enumNames[index]);
      }
    });

    roleItemsSchema.enum = filteredRoleEnums;
    if (Array.isArray(enumNames)) {
      roleItemsSchemaWithEnumNames.enumNames = filteredRoleEnumNames;
    }
  }

  customValidate(formData, errors) {
    // The number is stripped of non-numeric characters before it is submitted, so validate the
    // same value the API will receive rather than the raw input.
    const phoneNumber = sanitisePhoneNumber(formData.phone);

    // An empty phone number is allowed by the API, so only validate once something has been
    // entered - otherwise every user without a phone number becomes unsaveable.
    if (formData.phone_prefix && phoneNumber) {
      const regionCode = this.getRegionCodeFromPhonePrefix(formData.phone_prefix);

      const isPhoneNumberValid = isPhoneNumberValidForRegion({
        phoneNumber,
        regionCode,
      });

      if (!isPhoneNumberValid) {
        errors.phone?.addError('Phone number is not valid for the selected region/phone prefix');
      }
    }

    const roles = Array.isArray(formData.roles) ? formData.roles : [];
    const disallowedRoleMessage = this.getDisallowedRoleMessage(roles);
    if (disallowedRoleMessage) {
      errors.roles.addError(disallowedRoleMessage);
    }

    this.validateTravelPreferenceFields(formData.travel_preferences, errors.travel_preferences);

    return errors;
  }

  validateTravelPreferenceFields(preferences: TravelPreferencesForm | undefined, errors) {
    if (!errors) {
      return;
    }

    const travelPreferenceErrors = validateTravelPreferences(preferences);

    Object.entries(travelPreferenceErrors.frequentFlyerRows).forEach(([index, rowErrors]) => {
      const rowError = errors.frequent_flyer_numbers?.[index];
      if (rowErrors.airline) {
        rowError?.airline?.addError(rowErrors.airline);
      }
      if (rowErrors.number) {
        rowError?.number?.addError(rowErrors.number);
      }
    });

    if (travelPreferenceErrors.frequentFlyerNumbers) {
      errors.frequent_flyer_numbers?.addError(travelPreferenceErrors.frequentFlyerNumbers);
    }
    if (travelPreferenceErrors.seatPreference) {
      errors.seat_preference?.addError(travelPreferenceErrors.seatPreference);
    }
    if (travelPreferenceErrors.specialRequests) {
      errors.special_requests?.addError(travelPreferenceErrors.specialRequests);
    }
  }

  getRegionCodeFromPhonePrefix(phonePrefix?: string | null) {
    if (!phonePrefix) {
      return undefined;
    }
    const phonePrefixParts = phonePrefix.split('_');
    let selectedRegionCode;

    if (phonePrefixParts.length > 1) {
      // e.g. in the format 1_US or 1_CA, we want to get the 'US' or 'CA'
      selectedRegionCode = phonePrefixParts[1];
    } else {
      const selectedRegion = Object.keys(REGIONS_COUNTRY_CODES).find(
        (regionCode) => REGIONS_COUNTRY_CODES[regionCode].countryCode === phonePrefixParts[0],
      );

      selectedRegionCode = selectedRegion;
    }

    return selectedRegionCode;
  }

  handleStateData = () => {
    const schema = cloneDeep(this.state.schema);
    const formData = { ...this.state.formData };
    if (this.context.user.roles.includes(ROLE_ICS_STORE_TEAM)) {
      delete schema.properties.shadow_ban_user;
    }

    const roles = Array.isArray(formData.roles) ? formData.roles : [];
    if (!Array.isArray(formData.roles)) {
      formData.roles = roles;
    }

    this.filterDisallowedRoleOptions(schema, roles);
    const rolesEnumNames = (schema.properties.roles as StrictRJSFSchema)?.items?.['enumNames'];

    const businessDetailsSchema: StrictRJSFSchema = {
      type: 'object',
      title: 'Business details',
      properties: {
        abn: { type: ['string', 'null'], title: 'ABN', maxLength: 255 },
        businessName: { type: ['string', 'null'], title: 'Business name', maxLength: 255 },
        businessPosition: {
          type: ['string', 'null'],
          title: 'Position in business',
          maxLength: 255,
        },
        businessAddress: {
          type: ['object', 'null'],
          title: 'Business address',
          properties: {
            unit_number: { type: ['string', 'null'] },
            street: { type: ['string', 'null'] },
            city: { type: ['string', 'null'] },
            state: { type: ['string', 'null'] },
            postcode: { type: ['string', 'null'] },
            country: { type: ['string', 'null'] },
            formatted_address: { type: ['string', 'null'] },
          },
        },
      },
    };

    const languageOptions = getLanguageOptions(this.props.languageOptions ?? [], formData.language);

    //reset password
    schema.properties = {
      ...schema.properties,
      title: {
        type: 'string',
        title: 'Title',
        enum: ['Mr', 'Ms', 'Mrs', 'Miss', 'Master'],
        default: 'Mr',
      },
      email_verified: { type: 'boolean', title: 'Email Verified' },
      account_enabled: {
        type: 'boolean',
        title: this.canToggleAccountEnabled() ? 'Account Enabled' : 'Account Enabled (requires Role Admin)',
      },
      resetPassword: {
        type: 'boolean',
        title: 'Reset Password',
      },
      customer_support_code: {
        type: 'string',
        title: 'Customer Support Code',
      },
      roles: {
        ...(schema.properties.roles as object),
        items: {
          ...((schema.properties.roles as StrictRJSFSchema).items as object),
          title: 'Role',
        },
      },
      recently_used_airport_code: {
        ...(schema.properties.recently_used_airport_code as object),
        type: ['string', 'null'],
      },
      postcode: {
        ...(schema.properties.postcode as object),
        type: ['string', 'null'],
      },
      phone_prefix: {
        ...(schema.properties.phone_prefix as object),
        type: ['string', 'null'],
      },
      language: {
        type: 'string',
        title: 'Language',
        oneOf: languageOptions.map(({ value, label }) => ({
          type: 'string',
          title: label,
          enum: [value],
        })),
      },
      business_details: businessDetailsSchema,
    };

    const travelPreferencesEnabled = this.props.initialTravelPreferences !== undefined;
    if (travelPreferencesEnabled) {
      schema.properties.travel_preferences = travelPreferencesSchema;
    }

    const uiSchema: UiSchema = {
      ...initUiSchema,
      roles: {
        ...(initUiSchema.roles as object),
        items: {
          'ui:enumNames': rolesEnumNames,
        },
      },
    };

    delete schema.properties.password;

    // reset password
    if (formData.resetPassword) {
      schema.properties = {
        ...schema.properties,
        password: { type: 'string', title: 'Password' },
      };
    } else {
      delete formData.password;
      delete schema.properties.password;
    }

    const businessFieldColSpan = this.props.groupedSections ? 4 : 3;
    uiSchema.business_details = {
      'ui:colSpan': 12,
      'ui:options': { gridLayout: true },
      'ui:order': ['abn', 'businessName', 'businessPosition', 'businessAddress'],
      abn: { 'ui:colSpan': businessFieldColSpan },
      businessName: { 'ui:colSpan': businessFieldColSpan },
      businessPosition: { 'ui:colSpan': businessFieldColSpan },
      businessAddress: { 'ui:field': 'BusinessAddressField', 'ui:colSpan': 12 },
    };

    if (this.props.groupedSections) {
      uiSchema.roles = { ...uiSchema.roles, 'ui:options': { ...uiSchema.roles['ui:options'], compact: true } };
      uiSchema.business_details['ui:title'] = '';
      uiSchema.partnerships = { ...uiSchema.partnerships, 'ui:title': '' };
      uiSchema.resetPassword = { ...uiSchema.resetPassword, 'ui:widget': ResetPasswordWidget };
      uiSchema.password = { 'ui:widget': 'password', 'ui:help': 'Use Save changes to apply the new password.' };
    }

    if (travelPreferencesEnabled) {
      uiSchema.travel_preferences = {
        ...travelPreferencesUiSchema,
        'ui:disabled': this.props.isTravelPreferencesLoading,
      };

      if (!formData.travel_preferences) {
        formData.travel_preferences = cloneDeep(this.props.initialTravelPreferences);
      }
    }

    if (!formData.business_details) {
      const initialBusinessDetails = this.props.initialBusinessDetails;
      formData.business_details = {
        abn: initialBusinessDetails?.abn ?? null,
        businessName: initialBusinessDetails?.businessName ?? null,
        businessPosition: initialBusinessDetails?.businessPosition ?? null,
        businessAddress: initialBusinessDetails?.businessAddress ?? null,
      };
    }

    delete formData.custom_claims;
    delete schema.properties.custom_claims;

    //partnerships
    if (window.configs.KRIS_FLYER_ENABLED !== 'true') {
      delete (schema.properties.partnerships as StrictRJSFSchema).properties.kfp;
    }

    if (window.configs.QANTAS_EARN_ORDER_BASED_ENABLED === 'true') {
      delete (schema.properties.partnerships as StrictRJSFSchema).properties.qff;
    }

    //vendor
    if (!roles.includes('vendor-user')) {
      schema.properties = { ...schema.properties };
      if (formData.vendors) {
        delete formData.vendors;
      }
      if (schema.properties.vendors) {
        delete schema.properties.vendors;
      }
    } else {
      schema.properties = {
        ...schema.properties,
        vendors: {
          type: 'array',
          title: 'Vendor ID',
          items: { type: 'string' },
        },
      };
    }

    if (roles.length === 0 && formData.status === 'ENABLED') {
      formData.roles = ['customer'];
    }

    if (formData.gdpr) {
      formData.gdpr = true;
    } else {
      delete formData.gdpr;
    }

    if (!formData.phone) {
      delete formData.phone;
    }

    if (!formData.country_code) {
      delete formData.country_code;
    }

    if (!formData.dob) {
      delete formData.dob;
    }

    if (this.context.user.roles.includes(ROLE_ICS_STORE_TEAM)) {
      uiSchema.dob = { ...uiSchema.dob, 'ui:widget': 'hidden' };
    }

    uiSchema.banned_led_user = { 'ui:widget': 'hidden' };

    // Fills the 2 columns left free on the Country/Postcode/Language/Airport/Reset Password row.
    uiSchema.account_enabled = { 'ui:colSpan': 2, 'ui:disabled': !this.canToggleAccountEnabled() };

    const selectedRegionCode = this.getRegionCodeFromPhonePrefix(formData.phone_prefix);
    const phoneNumberHelpMessage =
      PHONE_FORMAT_HELP_MESSAGE_BY_REGION[selectedRegionCode] || DEFAULT_PHONE_FORMAT_HELP_MESSAGE;
    uiSchema.phone = {
      ...uiSchema.phone,
      'ui:help': phoneNumberHelpMessage,
    };

    if (this.props.linkedAccountEmail !== undefined) {
      schema.properties = {
        ...schema.properties,
        linked_personal_account: { type: 'string', title: 'Linked personal account' },
      };
      formData.linked_personal_account = this.props.linkedAccountEmail;
      const existingOrder = uiSchema['ui:order'] || Object.keys(schema.properties);
      const phoneIndex = existingOrder.indexOf('phone');
      const insertAt = phoneIndex >= 0 ? phoneIndex + 1 : existingOrder.length;
      uiSchema['ui:order'] = [
        ...existingOrder.slice(0, insertAt),
        'linked_personal_account',
        ...existingOrder.slice(insertAt),
      ];
      uiSchema.linked_personal_account = {
        'ui:field': 'LinkedAccountField',
        'ui:options': { creditCustomerId: this.props.linkedAccountId },
      };
    }

    return { schema, formData, uiSchema };
  };

  handleChange = (data: IChangeEvent<FormData>, fieldId?: string) => {
    const formData = data.formData;

    // RJSF also emits changes while applying defaults. Only an actual field edit
    // starts a draft, so merely opening details never blocks section navigation.
    if (fieldId && this.draftBaseline === undefined) {
      this.draftBaseline = cloneDeep(this.handleStateData().formData);
    }

    this.setState({
      formData,
      alertSuccess: false,
      alertError: false,
      errorMessage: '',
      partnershipsAlertError: false,
      partnershipsErrorMessage: '',
      flightEnabled: formData.flights_enabled,
      referralProgramEnabled: formData.referral_program_enabled,
      shadowBanUser: formData.shadow_ban_user,
    });
  };

  handleSubmit = async (event: IChangeEvent<FormData>) => {
    const {
      business_details,
      travel_preferences,
      custom_claims: _customClaims,
      language,
      ...restFormData
    } = event.formData;

    const formData = {
      ...restFormData,
    } as FormData;

    const businessDetailsPayload = formToApiBusinessDetails(business_details);
    const businessDetailsForState = apiToFormBusinessDetails(businessDetailsPayload);
    const travelPreferencePatches = diffTravelPreferencePatches(
      travel_preferences,
      this.props.initialTravelPreferences,
    );
    const shouldUpdateTravelPreferences = travelPreferencePatches.length > 0;
    delete formData.flights_enabled;
    delete formData.referral_program_enabled;
    delete formData.shadow_ban_user;
    delete formData.linked_personal_account;
    const accountEnabled = formData.account_enabled;
    delete formData.account_enabled;
    const shouldUpdateShadowBanUser =
      typeof this.state.shadowBanUser === 'boolean' && this.state.shadowBanUser !== this.props.user.shadow_ban_user;
    const shouldUpdateAccountEnabled =
      this.canToggleAccountEnabled() &&
      typeof accountEnabled === 'boolean' &&
      accountEnabled !== this.state.accountEnabled;

    if (!formData.dob) {
      formData.dob = null;
    }

    if (formData.phone) {
      formData.phone = sanitisePhoneNumber(formData.phone);
    }

    let user = formData as App.User;
    let userSaved = false;
    let businessDetailsSaved;
    try {
      user = await UsersService.updateUser(formData, this.props.brand);

      let toggleFlight: unknown = true;
      if (this.state.flightEnabled !== this.props.user.flights_enabled) {
        toggleFlight = await UsersService.toggle({
          id_member: formData.id_member,
          toggle_name: 'flights_enabled',
          toggle_value: this.state.flightEnabled,
        });
      }

      let toggleReferralProgram: unknown = true;
      if (this.state.referralProgramEnabled !== this.props.user.referral_program_enabled) {
        toggleReferralProgram = await UsersService.toggle({
          id_member: formData.id_member,
          toggle_name: 'referral_program_enabled',
          toggle_value: this.state.referralProgramEnabled,
        });
      }

      let toggleShadowBanUser;
      if (shouldUpdateShadowBanUser) {
        toggleShadowBanUser = await UsersService.toggle({
          id_member: formData.id_member,
          toggle_name: 'shadow_ban_user',
          toggle_value: this.state.shadowBanUser,
        });
      }

      await UsersService.updateUserBusinessDetails(formData.id_member, businessDetailsPayload);
      if (language) {
        await UsersService.setCustomerLanguagePreference(formData.id_member, language);
      } else {
        await UsersService.deleteCustomerLanguagePreference(formData.id_member);
      }
      if (shouldUpdateTravelPreferences) {
        await UsersService.patchCustomerProperties(formData.id_member, travelPreferencePatches);
      }
      businessDetailsSaved = true;

      // Runs last: disabling invalidates the customer's live tokens, so it only
      // fires once every other mutation has succeeded. A failure throws into the
      // catch below like any other save error.
      if (shouldUpdateAccountEnabled) {
        await UsersService.setAccountEnabled(formData.id_member, accountEnabled);
      }

      if (
        user &&
        toggleFlight &&
        toggleReferralProgram &&
        businessDetailsSaved &&
        (!shouldUpdateShadowBanUser || (shouldUpdateShadowBanUser && toggleShadowBanUser))
      ) {
        userSaved = true;
      }
    } catch (err) {
      const errorMessage = getApiErrorMessage(err);
      this.setState({
        alertSuccess: false,
        alertError: true,
        errorMessage: errorMessage,
      });
    }

    if (userSaved && !isEqual(formData.partnerships, this.props.user.partnerships)) {
      try {
        await UsersService.updatePartnershipsDetails(formData.id_member, formData.partnerships);
      } catch (err) {
        const errorMessage = getApiErrorMessage(err);
        this.setState({
          partnershipsAlertError: true,
          partnershipsErrorMessage: errorMessage,
        });
      }
    }

    if (userSaved) {
      user = await UsersService.getUser(formData.id_member, {
        brand: this.props.brand,
      });
      user.language = await UsersService.getCustomerLanguagePreference(formData.id_member);
      const nextFormData: FormData = {
        ...cloneDeep(user),
        language: user.language ?? '',
        business_details: businessDetailsForState,
        travel_preferences: cloneDeep(travel_preferences),
        account_enabled: user.status === 'ENABLED',
      };

      this.draftBaseline = undefined;

      this.setState(
        {
          formData: nextFormData,
          accountEnabled: user.status === 'ENABLED',
          alertSuccess: true,
          alertError: false,
          errorMessage: '',
        },
        () => {
          if (this.props?.update) {
            this.props.update(user);
          }
          if (this.props.onBusinessDetailsSaved) {
            this.props.onBusinessDetailsSaved();
          }
          if (shouldUpdateTravelPreferences && this.props.onTravelPreferencesSaved) {
            this.props.onTravelPreferencesSaved();
          }
          //this.props.update causes handleChange to fire, which resets alertSuccess
          this.setState({
            alertSuccess: true,
          });
        },
      );
    }
  };

  nativeCurrencyCode() {
    const region = libRegions.getRegionByCode(this.state.formData.country_code);
    return region ? region.currencyCode : 'Currency is not available as country is not set';
  }

  render() {
    const alertError = (
      <Alert severity="error">
        <strong>Error!</strong> Unable to save user. {this.state.errorMessage}.
      </Alert>
    );

    const alertSuccess = (
      <Alert severity="success">
        <strong>Success!</strong> User Information Saved
      </Alert>
    );

    const partnershipsAlertError = (
      <Alert severity="error">
        <strong>Error!</strong> Unable to save partnership details. {this.state.partnershipsErrorMessage}.
      </Alert>
    );

    const { schema, formData, uiSchema } = this.handleStateData();

    return (
      <Box sx={{ mt: this.props.groupedSections ? 0 : 4 }}>
        {this.props.groupedSections && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="h2" component="h2">
              User details
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Account created {formatDateOrdinalWithClock(this.props.user.created_date)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Native currency: {this.nativeCurrencyCode()}
            </Typography>
          </Box>
        )}
        {this.state.alertError && alertError}
        {this.state.alertSuccess && alertSuccess}
        {this.state.partnershipsAlertError && partnershipsAlertError}
        <Form
          formData={formData}
          schema={schema}
          uiSchema={uiSchema}
          templates={this.props.groupedSections ? GROUPED_FORM_TEMPLATES : FORM_TEMPLATES}
          onSubmit={this.handleSubmit}
          onChange={this.handleChange}
          validator={validator}
          customValidate={this.customValidate}
          fields={customFields}
          widgets={customWidgets}
        >
          {!this.props.groupedSections && (
            <Box component="dl">
              <Typography component="dt" variant="overline" color="text.secondary">
                Native Currency Code
              </Typography>
              <Typography component="dd" sx={{ m: 0 }}>
                {this.nativeCurrencyCode()}
              </Typography>
            </Box>
          )}

          <Button
            type="submit"
            ref={(btn) => {
              this.submitButton = btn;
            }}
            sx={{ display: 'none' }}
          />
        </Form>
        {this.state.alertError && alertError}
        {this.state.alertSuccess && alertSuccess}
        {this.state.partnershipsAlertError && partnershipsAlertError}
      </Box>
    );
  }
}
